<template>
  <!-- Bare layout for public customer pages (e.g. /j/<token>) — no portal chrome. -->
  <div v-if="bareLayout" class="min-h-screen">
    <RouterView />
    <ToastContainer />
    <RequestLoader />
  </div>

  <div v-else class="min-h-screen flex flex-col">
    <header class="client-header sticky top-0 z-30 backdrop-blur bg-surface/80 border-b border-border">
      <RouterLink to="/" class="brand-link" :title="t('app.name')">
        <img v-if="logoUrl" :src="logoUrl" :alt="companyName" class="auth-brand-logo" />
        <BrandLogo v-else :height="64" />
      </RouterLink>
      <nav class="client-header__nav">
        <button
          type="button"
          class="locale-btn"
          @click="toggleLocale"
        >{{ locale === "en" ? t("common.arabic") : t("common.english") }}</button>
        <RouterLink
          v-if="auth.user && auth.has('docs.user_manual.view')"
          to="/help/manual"
          class="help-btn"
          :title="t('help.title')"
        >
          <i class="pi pi-question-circle" aria-hidden="true" />
        </RouterLink>
        <ThemeToggle />
        <UserMenu
          v-if="auth.user"
          :user="auth.user"
          :sign-out-label="t('auth.signOut')"
          @sign-out="onSignOut"
        >
          <template #default="{ close }">
            <li>
              <RouterLink to="/profile" class="user-menu__item" @click="close">
                {{ t("auth.profile") }}
              </RouterLink>
            </li>
          </template>
        </UserMenu>
      </nav>
    </header>

    <main class="flex-1">
      <RouterView />
    </main>

    <AppFooter
      :copyright="t('appFooter.copyright', { year, company: companyName })"
      :version="appVersion"
      :support-email="supportEmail"
    />

    <CommandPalette />
    <ToastContainer />
    <ConfirmDialog />
    <RequestLoader />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { applyDocumentLocale, persistLocale, type Locale } from "@ram/i18n";
import {
  AppFooter,
  BrandLogo,
  CommandPalette,
  ConfirmDialog,
  RequestLoader,
  ThemeToggle,
  ToastContainer,
  UserMenu,
  useCommandPalette,
} from "@ram/ui";
import { useAuthStore } from "@/stores/auth";
import { api } from "@/lib/api";

const { t, locale } = useI18n();
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const bareLayout = computed(() => route.meta.bare === true);
useCommandPalette(); // installs the global ⌘K listener

// Clear lingering auth errors when navigating between auth pages — without
// this the message from /login bleeds onto /signup until the user types.
watch(
  () => route.fullPath,
  () => {
    auth.error = null;
  },
);

function toggleLocale() {
  const next: Locale = locale.value === "en" ? "ar" : "en";
  locale.value = next;
  applyDocumentLocale(next);
  persistLocale(next);
}

async function onSignOut() {
  await auth.logout();
  await router.replace({ name: "login" });
}

const year = computed(() => new Date().getFullYear());
const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "v0.1.0";
const companyName = ref("Rafid Auto-Maintenance");
const shortName   = ref("Rafid");
provide("company",    companyName);
provide("shortName",  shortName);
const supportEmail = ref((import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? "support@rafid.ae");
const logoUrl = ref<string | null>(null);

api.get<{ company_name: string; short_name: string; support_email: string; logo_url: string | null }>("/api/v1/config/")
  .then(({ data }) => {
    if (data.company_name) companyName.value = data.company_name;
    if (data.short_name)   shortName.value   = data.short_name;
    if (data.support_email) supportEmail.value = data.support_email;
    logoUrl.value = data.logo_url ?? null;
  })
  .catch(() => { /* keep defaults */ });
</script>

<style scoped>
/* ── Header — direct flex (same structure as admin auth-frame header) ── */
.client-header {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ram-space-3);
  padding: 0 var(--ram-space-4);
}
.client-header__nav {
  display: flex;
  align-items: center;
  gap: var(--ram-space-2);
}
.locale-btn {
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: var(--ram-text-sm);
  color: var(--ram-color-text-secondary);
  border-radius: var(--ram-radius-md);
  transition: background var(--ram-duration-fast);
}
.locale-btn:hover { background: var(--ram-color-surface-muted); }

/* ── Header link alignment ─────────────────────────────────────────── */
/* All <a> tags in the header are flex containers so their click area   */
/* matches the visual bounds of the icon/image they wrap.               */
.brand-link {
  display: flex;
  align-items: center;
}


.help-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px; height: 36px;
  border-radius: var(--ram-radius-md);
  color: var(--ram-color-text-secondary);
  text-decoration: none;
  transition: background var(--ram-duration-fast), color var(--ram-duration-fast);
  font-size: 18px;
}
.help-btn:hover {
  background: var(--ram-color-surface-muted);
  color: var(--ram-color-text-primary);
}
</style>
